/**
 * HubPage — Reusable hub overview component.
 * Reads from SECONDARY_NAV_SECTIONS and renders a full-width card grid.
 * Supports tier: "utility" for compact hulpmiddelen rendering, and ai/beta badges.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useUserRole } from '../contexts/UserRoleContext';
import { useVisibilityFilters, isItemVisible } from '../contexts/VisibilityFiltersContext';
import { SECONDARY_NAV_SECTIONS } from '../config/navigation';
import PageHeader from './PageHeader';

const T = {
  primary: '#526075', tertiary: '#635983', tertiaryContainer: '#dbcdff',
  primaryContainer: '#d5e3fd', surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

// Een item met `externalUrl` (bv. Formaat omzetten in Flow) opent in een nieuw tabblad.
const linkProps = (item) => (item.externalUrl
  ? { component: 'a', href: item.externalUrl, target: '_blank', rel: 'noopener noreferrer' }
  : { component: Link, to: item.path });

const ExternIcoon = ({ item }) => (item.externalUrl
  ? <Box component="span" title="Opent in een nieuw tabblad" sx={{ display: 'inline-flex', ml: 0.5, flexShrink: 0, color: '#adb3b5' }}><Icon name="open_in_new" size={13} /></Box>
  : null);

const DEFAULT_GROUP_ICONS = {
  Plannen: 'auto_awesome',
  'Werkproces': 'check_circle', 'Hulpmiddelen': 'handyman',
  'Mijn werk': 'person', 'Team': 'groups', 'Resources': 'inventory_2',
  'Pre-productie': 'movie_edit', 'Post-productie': 'movie',
  'Distributie': 'send', 'Export & Tools': 'build', 'Materialen': 'inventory_2',
  'Commercieel': 'trending_up', 'Financieel': 'account_balance', 'Kwaliteit': 'star',
  'Assistent': 'chat', 'Analyse': 'analytics', 'Creatief': 'palette',
  'AI Tools': 'auto_awesome', 'Experimenten': 'science', 'Beta': 'science',
};

const DEFAULT_GROUP_ACCENT = {
  Plannen: { color: '#6d28d9', bg: '#ede9fe' },
  'Werkproces': { color: '#526075', bg: '#d5e3fd' },
  'Hulpmiddelen': { color: '#767c7e', bg: '#f1f4f5' },
  'Mijn werk': { color: '#3b82f6', bg: '#dbeafe' },
  'Team': { color: '#526075', bg: '#d5e3fd' },
  'Resources': { color: '#10b981', bg: '#ecfdf5' },
  'Pre-productie': { color: '#3b82f6', bg: '#dbeafe' },
  'Post-productie': { color: '#635983', bg: '#dbcdff' },
  'Distributie': { color: '#f59e0b', bg: '#fef3c7' },
  'Export & Tools': { color: '#f59e0b', bg: '#fef3c7' },
  'Materialen': { color: '#10b981', bg: '#ecfdf5' },
  'Commercieel': { color: '#3b82f6', bg: '#dbeafe' },
  'Financieel': { color: '#f59e0b', bg: '#fef3c7' },
  'Kwaliteit': { color: '#10b981', bg: '#ecfdf5' },
  'AI Tools': { color: '#635983', bg: '#dbcdff' },
  'Experimenten': { color: '#7c3aed', bg: '#ede9fe' },
  'Beta': { color: '#7c3aed', bg: '#ede9fe' },
};

const FALLBACK_ACCENT = { color: '#767c7e', bg: '#f1f4f5' };

function BadgeAI() {
  return (
    <Box sx={{
      fontSize: '0.5rem', fontWeight: 800, fontFamily: '"Inter", system-ui, sans-serif',
      color: '#526075', bgcolor: '#d5e3fd',
      px: 0.75, py: 0.2, borderRadius: '4px',
      textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.4,
    }}>
      AI
    </Box>
  );
}

function BadgeBeta() {
  return (
    <Box sx={{
      fontSize: '0.5rem', fontWeight: 800, fontFamily: '"Inter", system-ui, sans-serif',
      color: '#7c3aed', bgcolor: '#ede9fe',
      px: 0.75, py: 0.2, borderRadius: '4px',
      textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.4,
    }}>
      Beta
    </Box>
  );
}

function BadgePlanned() {
  return (
    <Box sx={{
      fontSize: '0.5rem', fontWeight: 800, fontFamily: '"Inter", system-ui, sans-serif',
      color: '#d97706', bgcolor: '#fef3c7',
      px: 0.75, py: 0.2, borderRadius: '4px',
      textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.4,
    }}>
      Planned
    </Box>
  );
}

export default function HubPage({ sectionKey, title, subtitle, headerIcon, headerAccent, itemMeta = {} }) {
  const { permissions } = useUserRole();
  const visibility = useVisibilityFilters();
  const section = SECONDARY_NAV_SECTIONS[sectionKey];
  if (!section) return null;

  const accentBg = headerAccent?.bg || T.primaryContainer;
  const accentColor = headerAccent?.color || T.primary;

  // Build groups, separating process from utility
  const processGroups = [];
  const utilityItems = [];
  const groupMap = {};

  section.items.forEach(item => {
    if (item.path === section.hubPath) return;
    if (item.feature && !permissions?.[item.feature]?.view) return;
    if (!isItemVisible(item, visibility)) return;

    if (item.tier === 'utility' || item.group === 'Hulpmiddelen') {
      utilityItems.push(item);
    } else {
      const group = item.group || 'Overig';
      if (!groupMap[group]) {
        groupMap[group] = { label: group, items: [] };
        processGroups.push(groupMap[group]);
      }
      groupMap[group].items.push(item);
    }
  });

  return (
    <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10 }}>

      <PageHeader
        icon={headerIcon || 'dashboard'}
        title={title || section.label}
        subtitle={subtitle}
        accent={{ color: accentColor, bg: accentBg }}
      />

      {/* Empty state */}
      {processGroups.length === 0 && utilityItems.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Icon name="lock" size={48} sx={{ color: T.outlineVariant, display: 'block', margin: '0 auto 16px' }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.92rem', color: T.outline }}>
            Geen tools beschikbaar voor jouw rol
          </Typography>
        </Box>
      )}

      {/* Process groups — full cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {processGroups.map((group) => {
          const isBeta = group.label === 'Beta';
          const accent = DEFAULT_GROUP_ACCENT[group.label] || FALLBACK_ACCENT;

          return (
            <Box key={group.label}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '0.5rem',
                  bgcolor: accent.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={DEFAULT_GROUP_ICONS[group.label] || 'folder'} size={16} sx={{ color: accent.color }} />
                </Box>
                <Typography sx={{
                  fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700,
                  color: T.text, letterSpacing: '-0.01em',
                }}>
                  {group.label}
                </Typography>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant }}>
                  {group.items.length} {group.items.length === 1 ? 'tool' : 'tools'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: `repeat(${Math.min(group.items.length, 4)}, 1fr)`,
                },
                gap: 1.5,
              }}>
                {group.items.map((item) => {
                  const meta = itemMeta[item.path] || { icon: 'link', desc: '' };
                  return (
                    <Box
                      key={item.path}
                      {...linkProps(item)}
                      sx={{
                        display: 'flex', flexDirection: 'column', gap: 1.5,
                        textDecoration: 'none', color: 'inherit',
                        bgcolor: T.surface, borderRadius: '0.75rem', p: 2.5,
                        border: isBeta ? '1px solid rgba(124,58,237,0.12)' : '1px solid rgba(45,51,53,0.06)',
                        opacity: item.planned ? 0.5 : 1,
                        transition: 'all 0.15s',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(45,51,53,0.07)',
                          borderColor: isBeta ? 'rgba(124,58,237,0.25)' : `${T.primary}25`,
                          transform: 'translateY(-2px)',
                          opacity: 1,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{
                          width: 40, height: 40, borderRadius: '0.625rem',
                          bgcolor: isBeta ? '#ede9fe' : accent.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Icon name={meta.icon} size={22} sx={{ color: isBeta ? '#7c3aed' : accent.color }} />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {item.ai && <BadgeAI />}
                          {item.planned && <BadgePlanned />}
                          {item.beta && <BadgeBeta />}
                        </Box>
                      </Box>
                      <Box>
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.88rem', fontWeight: 700, color: T.text, mb: 0.25, display: 'flex', alignItems: 'center' }}>
                          {item.label}
                          <ExternIcoon item={item} />
                        </Typography>
                        {meta.desc && (
                          <Typography sx={{
                            fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, lineHeight: 1.45,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          }}>
                            {meta.desc}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Utility items — compact row */}
      {utilityItems.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{
              width: 28, height: 28, borderRadius: '0.5rem',
              bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon name="handyman" size={16} sx={{ color: T.outlineVariant }} />
            </Box>
            <Typography sx={{
              fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700,
              color: T.text, letterSpacing: '-0.01em',
            }}>
              Hulpmiddelen
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.outlineVariant }}>
              {utilityItems.length} tools
            </Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 1,
          }}>
            {utilityItems.map((item) => {
              const meta = itemMeta[item.path] || { icon: 'link', desc: '' };
              return (
                <Box
                  key={item.path}
                  {...linkProps(item)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    textDecoration: 'none', color: 'inherit',
                    bgcolor: T.surface, borderRadius: '0.625rem',
                    px: 2, py: 1.25,
                    border: '1px solid rgba(45,51,53,0.06)',
                    opacity: item.planned ? 0.5 : 1,
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: `${T.primary}20`,
                      boxShadow: '0 4px 12px rgba(45,51,53,0.05)',
                      opacity: 1,
                    },
                  }}
                >
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '0.5rem',
                    bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon name={meta.icon} size={18} sx={{ color: T.outline }} />
                  </Box>
                  <Typography noWrap sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', fontWeight: 600, color: T.text, flex: 1 }}>
                    {item.label}
                  </Typography>
                  <ExternIcoon item={item} />
                  {item.ai && <BadgeAI />}
                  {item.planned && <BadgePlanned />}
                          {item.beta && <BadgeBeta />}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}
